import { UzButton, UzDialog, UzInput } from '@utazzo/ui';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CUSTOM_PAGES_CONFIG, type PageScreenConfig } from '../page-screen-config';
import { useCreatePage } from '../use-pages';

interface NewPageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which page-type surface this dialog creates for. Defaults to Custom Pages. */
  config?: PageScreenConfig;
}

/** Title → kebab-case slug (lowercase letters, digits, dashes). */
const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Create a new page of the configured type. Collects a title (slug auto-derived,
 * still editable) and creates a DRAFT page through the type-scoped endpoint,
 * then opens it in the editor at the type's route.
 */
export const NewPageDialog = ({
  open,
  onOpenChange,
  config = CUSTOM_PAGES_CONFIG,
}: NewPageDialogProps): React.JSX.Element => {
  const navigate = useNavigate();
  const create = useCreatePage(config.basePath);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);

  const reset = (): void => {
    setTitle('');
    setSlug('');
    setSlugEdited(false);
  };

  const effectiveSlug = slugEdited ? slug : slugify(title);
  const canCreate = title.trim().length > 0 && effectiveSlug.length > 0 && !create.isPending;

  const submit = async (): Promise<void> => {
    if (!canCreate) return;
    const page = await create.mutateAsync({
      pageType: config.createPageType,
      title: title.trim(),
      slug: effectiveSlug,
      showInSitemap: true,
      noIndex: false,
    });
    reset();
    onOpenChange(false);
    navigate(`${config.editorBase}/${page.id}`);
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
      title={config.createDialogTitle}
      description={config.createDialogDescription}
      footer={
        <>
          <UzButton variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </UzButton>
          <UzButton onClick={() => void submit()} loading={create.isPending} disabled={!canCreate}>
            Create page
          </UzButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-(--fg-muted)">Title</label>
          <UzInput
            autoFocus
            value={title}
            placeholder={config.createPlaceholder}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-(--fg-muted)">URL slug</label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-(--fg-faint)">/</span>
            <UzInput
              value={effectiveSlug}
              placeholder="summer-europe-offers"
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(slugify(e.target.value));
              }}
            />
          </div>
          <p className="mt-1 text-[12px] text-(--fg-faint)">
            Lowercase letters, digits and dashes. Auto-filled from the title.
          </p>
        </div>
      </div>
    </UzDialog>
  );
};
